import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Church, CheckCircle2, RefreshCw, Home, Plus } from "lucide-react";
import { toast } from "@/lib/toast";
import { getChurches, completeTransferOnboarding } from "../lib/api";
import { useDistrict } from "../lib/district-context";
import CreateIdentitySheet from "./pastoral/CreateIdentitySheet";

/**
 * Full-screen overlay shown when the pastor needs pastoral-home onboarding.
 *
 * "Sin hogar" (needsHomeSetup = setup_new o transferred SIN person_id/familia):
 *   - sin iglesias → CTA crear primera iglesia (pospone + navega)
 *   - con iglesias → CTA crear hogar (reusa CreateIdentitySheet → createPastoralIdentity)
 *   createPastoralIdentity limpia ministry_status='transferred' → no hay re-loop.
 *
 * mode='transferred' CON familia (estados B/C, intactos):
 *   B. family set, sin iglesias — distrito vacío; el pastor pospone
 *   C. family set, hay iglesias — flujo normal (completeTransferOnboarding)
 *
 * @param {Object|null} family     — familia flotante (transferred), o null
 * @param {string}      mode       — 'transferred' | 'setup_new'
 * @param {Function}    onComplete — tras asignar/crear con éxito
 * @param {Function}    onDismiss  — al posponer
 */
export default function FloatingWizard({ family, mode = "transferred", onComplete, onDismiss }) {
  const { district, userProfile, refreshProfile } = useDistrict();
  const nav = useNavigate();
  const [churches,   setChurches]   = useState([]);
  const [selected,   setSelected]   = useState("");
  const [saving,     setSaving]     = useState(false);
  const [loadingCh,  setLoadingCh]  = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  // "Sin hogar": setup_new (pastor nuevo) o transferred sin vínculo (estado A). Ambos crean
  // hogar. Las ramas con `family` poblada (transferred B/C) NO entran aquí.
  const needsHomeSetup = mode === "setup_new" || !family;

  useEffect(() => {
    if (!district?.id) return;
    setLoadingCh(true);
    getChurches(district.id)
      .then((list) => setChurches(list || []))
      .catch(() => {})
      .finally(() => setLoadingCh(false));
  }, [district?.id]);

  const handleAssign = async () => {
    if (!selected || saving) return;
    setSaving(true);
    try {
      await completeTransferOnboarding(selected);
      toast.success("Hogar pastoral asignado a iglesia");
      await refreshProfile?.();
      onComplete?.();
    } catch (err) {
      toast.error(err.message || "No se pudo asignar el hogar");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateChurch = () => {
    onDismiss?.();           // pospone (banner persiste) para no tapar el formulario
    nav("/churches/new");
  };

  const handleHomeCreated = async () => {
    setShowCreate(false);
    await refreshProfile?.(); // person_id queda poblado -> isFloating cae a false
    onComplete?.();
  };

  const districtName = district?.name ?? "este distrito";

  // ── sin hogar: crear hogar (setup_new + transferred estado A) ────────────────
  if (needsHomeSetup) {
    // El sheet de crear hogar es un BottomSheet (z-60). Para que quede visible se retorna
    // SOLO el sheet (sin el overlay z-200) mientras está abierto.
    if (showCreate) {
      return (
        <CreateIdentitySheet
          isOpen={showCreate}
          onClose={() => setShowCreate(false)}
          district={district}
          authEmail={userProfile?.email}
          defaultName={userProfile?.full_name}
          defaultPhone={userProfile?.phone}
          onCreated={handleHomeCreated}
        />
      );
    }

    const emptyDistrict = !loadingCh && churches.length === 0;

    return (
      <div className="fixed inset-0 z-[200] bg-gradient-to-br from-[#0b3b82] via-[#1b4f9c] to-[#2563eb] flex items-center justify-center p-5">
        <div className="w-full max-w-sm space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">
          <div className="text-center">
            <div className="mx-auto h-16 w-16 rounded-[20px] bg-white/15 flex items-center justify-center mb-4">
              <Home size={28} className="text-white" />
            </div>
            <h1 className="text-[22px] font-semibold text-white tracking-tight">
              Configura tu hogar pastoral
            </h1>
            <p className="text-[13px] text-blue-200 mt-1 leading-relaxed">
              {emptyDistrict
                ? `${districtName} aún no tiene iglesias. Crea la primera para registrar tu hogar pastoral.`
                : "Registra tu hogar pastoral y ánclalo a una iglesia de tu distrito."}
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-[20px] p-4">
            <p className="text-[13px] text-blue-100 leading-relaxed">
              {emptyDistrict
                ? "Crea tu primera iglesia y luego vuelve aquí para registrar tu hogar. Puedes continuar usando la aplicación mientras tanto."
                : "Crearemos tu registro pastoral y tu hogar, y los vincularemos a tu cuenta. Puedes continuar usando la aplicación mientras tanto."}
            </p>
          </div>

          {loadingCh ? (
            <div className="h-12 bg-white/10 rounded-[16px] animate-pulse" />
          ) : (
            <button
              onClick={emptyDistrict ? handleCreateChurch : () => setShowCreate(true)}
              className="w-full h-12 rounded-[16px] bg-white text-[#1b4f9c] font-semibold text-[15px] flex items-center justify-center gap-2 shadow-md transition-all duration-200 active:scale-[0.98]"
            >
              {emptyDistrict ? <Church size={16} /> : <Plus size={16} />}
              {emptyDistrict ? "Crear primera iglesia" : "Crear mi hogar"}
            </button>
          )}

          <button
            onClick={onDismiss}
            className="w-full h-12 rounded-[16px] bg-white/20 border border-white/30 text-white font-semibold text-[15px] flex items-center justify-center gap-2 shadow-md transition-all duration-200 active:scale-[0.98]"
          >
            Continuar por ahora
          </button>
        </div>
      </div>
    );
  }

  // ── transferred CON familia huérfana (estados B/C, intactos) ─────────────────
  // Aquí family está SIEMPRE poblada (el caso family=null lo maneja needsHomeSetup arriba).
  const emptyDistrict = !loadingCh && churches.length === 0;
  const normalFlow    = loadingCh || churches.length > 0;
  const familyName    = family?.name;

  return (
    <div className="fixed inset-0 z-[200] bg-gradient-to-br from-[#0b3b82] via-[#1b4f9c] to-[#2563eb] flex items-center justify-center p-5">
      <div className="w-full max-w-sm space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300">

        {/* Header */}
        <div className="text-center">
          <div className="mx-auto h-16 w-16 rounded-[20px] bg-white/15 flex items-center justify-center mb-4">
            <Home size={28} className="text-white" />
          </div>
          <h1 className="text-[22px] font-semibold text-white tracking-tight">
            Asigna tu hogar pastoral
          </h1>
          <p className="text-[13px] text-blue-200 mt-1 leading-relaxed">
            {`${familyName ? `"${familyName}"` : "Tu hogar"} fue transferido a ${districtName}. ${emptyDistrict ? "El distrito aún no tiene iglesias." : "Selecciona la iglesia a la que pertenece."}`}
          </p>
        </div>

        {/* Body — only shown in normal flow */}
        {normalFlow && (
          <div className="bg-white/10 backdrop-blur-sm rounded-[20px] p-4 space-y-3">
            <div className="flex items-center gap-2 mb-1">
              <Church size={14} className="text-blue-200" />
              <p className="text-[12px] font-semibold text-blue-200 uppercase tracking-wider">
                Iglesia
              </p>
            </div>
            {loadingCh ? (
              <div className="h-11 bg-white/10 rounded-[14px] animate-pulse" />
            ) : (
              <select
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                className="w-full h-11 rounded-[14px] bg-white/15 text-white border border-white/20 px-3 text-[14px] appearance-none focus:outline-none focus:ring-2 focus:ring-white/30"
              >
                <option value="" className="bg-blue-900">Seleccionar iglesia…</option>
                {churches.map((c) => (
                  <option key={c.id} value={c.id} className="bg-blue-900">{c.name}</option>
                ))}
              </select>
            )}
          </div>
        )}

        {/* Informational card — distrito vacío (estado B) */}
        {emptyDistrict && (
          <div className="bg-white/10 backdrop-blur-sm rounded-[20px] p-4">
            <p className="text-[13px] text-blue-100 leading-relaxed">
              Crea la primera iglesia desde el menú principal y luego vuelve aquí para completar la incorporación. Puedes continuar usando la aplicación mientras tanto.
            </p>
          </div>
        )}

        {/* CTA */}
        {normalFlow ? (
          <button
            onClick={handleAssign}
            disabled={!selected || saving}
            className="w-full h-12 rounded-[16px] bg-white text-[#1b4f9c] font-semibold text-[15px] flex items-center justify-center gap-2 shadow-md transition-all duration-200 active:scale-[0.98] disabled:opacity-40 disabled:pointer-events-none"
          >
            {saving
              ? <RefreshCw size={16} className="animate-spin" />
              : <CheckCircle2 size={16} />
            }
            {saving ? "Asignando…" : "Asignar iglesia"}
          </button>
        ) : (
          <button
            onClick={onDismiss}
            className="w-full h-12 rounded-[16px] bg-white/20 border border-white/30 text-white font-semibold text-[15px] flex items-center justify-center gap-2 shadow-md transition-all duration-200 active:scale-[0.98]"
          >
            Continuar por ahora
          </button>
        )}

      </div>
    </div>
  );
}
